import { Menu } from "@mui/material";
import { AttendanceReconfirmationStatusEnum, AttendanceStatusEnum } from "@sinch/core/entities/serverEnums";
import { isEmpty } from "ramda";
import React, { FC, useState } from "react";
import { ConfirmInvitationMenuItem } from "@sinch/components/application/ShiftDetailPanel/ActionMenu/ConfirmInvitationMenuItem";
import { ConfirmMessageMenuItem } from "@sinch/components/application/ShiftDetailPanel/ActionMenu/ConfirmMessageMenuItem";
import { ResendInvitationMenuItem } from "@sinch/components/application/ShiftDetailPanel/ActionMenu/ResendInvitationMenuItem";
import { SignOffMenuItem } from "@sinch/components/application/ShiftDetailPanel/ActionMenu/SignOffMenuItem";
import { ApplicantRejectMenuItem } from "@sinch/components/application/ShiftDetailPanel/ActionMenu/ApplicantRejectMenuItem";
import { ApplicantApproveMenuItem } from "@sinch/components/application/ShiftDetailPanel/ActionMenu/ApplicantApproveMenuItem";

export interface AttendanceActionMenuProps {
  event: React.MouseEvent<HTMLElement, MouseEvent>;
  allowedActions: MenuAction[];
  onUpdate?: () => void;
  preventDefault?: boolean;
}

interface MousePosition {
  x: number;
  y: number;
}

export enum ActionMenuActions {
  ResendInvitation = "RESEND_INVITATION",
  SignOff = "SIGN_OFF",
  ConfirmInvitation = "CONFIRM_INVITATION",
  ConfirmMessage = "CONFIRM_MESSAGE",
  ApplicantApprove = "APPLICANT_APPROVE",
  ApplicantReject = "APPLICANT_REJECT",
}

type ActionResendInvitation = {
  type: ActionMenuActions.ResendInvitation;
  data: {
    attendanceId: string;
  };
};
type ActionSignOff = {
  type: ActionMenuActions.SignOff;
  data: {
    attendanceId: string;
  };
};
type ActionConfirmInvitation = {
  type: ActionMenuActions.ConfirmInvitation;
  data: {
    attendanceId: string;
    attendanceStatus?: AttendanceStatusEnum;
  };
};
type ActionConfirmMessage = {
  type: ActionMenuActions.ConfirmMessage;
  data: {
    attendanceId: string;
    attendanceReconfirmationStatus?: AttendanceReconfirmationStatusEnum;
  };
};
type ActionApplicantApprove = {
  type: ActionMenuActions.ApplicantApprove;
  data: {
    applicantId: string;
  };
};
type ActionApplicantReject = {
  type: ActionMenuActions.ApplicantReject;
  data: {
    applicantId: string;
  };
};

export type MenuAction =
  | ActionResendInvitation
  | ActionSignOff
  | ActionConfirmInvitation
  | ActionConfirmMessage
  | ActionApplicantApprove
  | ActionApplicantReject;

interface ActionMenuProps {
  anchorEl: HTMLElement | MousePosition | null;
  onClose: () => void;
  actions: MenuAction[];
}

export const ActionMenu: FC<ActionMenuProps> = ({ actions, anchorEl, onClose: handleClose }) => {
  const [loading, setLoading] = useState<string | null>(null);
  const open = Boolean(anchorEl);

  const isHtmlElement = (anchor: HTMLElement | MousePosition | null): anchor is HTMLElement =>
    anchor instanceof HTMLElement;
  const isCoordinates = (anchor: HTMLElement | MousePosition | null): anchor is MousePosition =>
    !(anchor instanceof HTMLElement) && anchorEl !== null;

  return (
    <Menu
      anchorEl={isHtmlElement(anchorEl) ? anchorEl : undefined}
      anchorOrigin={isHtmlElement(anchorEl) ? { horizontal: "right", vertical: "bottom" } : undefined}
      anchorPosition={isCoordinates(anchorEl) ? { top: anchorEl.y + 8, left: anchorEl.x + 8 } : undefined}
      anchorReference={isCoordinates(anchorEl) ? "anchorPosition" : "anchorEl"}
      onClose={loading ? undefined : handleClose}
      open={open && !isEmpty(actions)}
      transformOrigin={isHtmlElement(anchorEl) ? { horizontal: "right", vertical: "top" } : undefined}
    >
      {actions.map((action) => {
        switch (action.type) {
          case ActionMenuActions.ResendInvitation:
            return (
              <ResendInvitationMenuItem
                key={action.type}
                attendanceId={action.data.attendanceId}
                handleClose={handleClose}
                loading={loading === "resendInvitation"}
                onLoading={() => setLoading("resendInvitation")}
              />
            );
          case ActionMenuActions.ConfirmInvitation:
            return (
              <ConfirmInvitationMenuItem
                key={action.type}
                attendanceId={action.data.attendanceId}
                handleClose={handleClose}
                loading={loading === "confirmInvitation"}
                onLoading={() => setLoading("confirmInvitation")}
              />
            );
          case ActionMenuActions.ConfirmMessage:
            return (
              <ConfirmMessageMenuItem
                key={action.type}
                attendanceId={action.data.attendanceId}
                handleClose={handleClose}
                loading={loading === "confirmMessage"}
                onLoading={() => setLoading("confirmMessage")}
              />
            );
          case ActionMenuActions.SignOff:
            return (
              <SignOffMenuItem
                key={action.type}
                attendanceId={action.data.attendanceId}
                handleClose={handleClose}
                loading={loading === "signOff"}
                onLoading={() => setLoading("signOff")}
              />
            );
          case ActionMenuActions.ApplicantApprove:
            return (
              <ApplicantApproveMenuItem
                key={action.type}
                applicantId={action.data.applicantId}
                handleClose={handleClose}
                loading={loading === "applicantApprove"}
                onLoading={() => setLoading("applicantApprove")}
              />
            );
          case ActionMenuActions.ApplicantReject:
            return (
              <ApplicantRejectMenuItem
                key={action.type}
                applicantId={action.data.applicantId}
                handleClose={handleClose}
                loading={loading === "applicantDecline"}
                onLoading={() => setLoading("applicantDecline")}
              />
            );
          default:
            return null;
        }
      })}
    </Menu>
  );
};
